import { useContext, useState } from "react";
import { SideModal, Input, Textarea, Button, Switch } from "components/ui";
import ModalHeading from "components/ui/Modal/ModalHeading/ModalHeading";
import SelectUI from "components/ui/SelectUI/SelectUI";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import { apiRoute } from "services";
import API from "services/lib/api";
import { dataQueryStatus } from "utils";
import { getErrorMessage } from "utils/helper";
import { GLOBAL_TICKET_STATUS_CATEGORY_OPTIONS } from "../../enum";

const { ERROR } = dataQueryStatus;

const emptyForm = () => ({
    globalCategory: GLOBAL_TICKET_STATUS_CATEGORY_OPTIONS[0].value,
    name: "",
    description: "",
    isActive: true,
});

const findOption = (options, value) =>
    options.find((option) => option.value === value) || null;

const CreateCustomTicketStatusModal = ({
    showModal,
    closeModal,
    handleSuccess,
}) => {
    const toastMessage = useContext(ToastContext);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleClose = () => {
        setForm(emptyForm());
        closeModal?.();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) {
            toastMessage("Ticket status name cannot be blank", ERROR);
            return;
        }

        try {
            setSaving(true);
            const res = await API.post(apiRoute.createCustomTicketStatus, {
                globalCategory: form.globalCategory,
                name: form.name.trim(),
                description: form.description?.trim() || undefined,
                isActive: form.isActive,
            });

            if (res.status === 200 || res.status === 201) {
                toastMessage(
                    res?.data?.message || "Custom ticket status created"
                );
                setForm(emptyForm());
                handleSuccess?.();
            }
        } catch (err) {
            toastMessage(getErrorMessage(err), ERROR);
        } finally {
            setSaving(false);
        }
    };

    return (
        <SideModal show={showModal} close={handleClose}>
            <div className='row me-0 ms-0'>
                <div className='col-10 offset-1'>
                    <ModalHeading
                        title='Create Ticket Status'
                        subTitle="Map a custom status to one of Metacare's global workflow stages."
                    />
                    <form className='mt-3' onSubmit={handleSubmit}>
                        <div className='mb-3'>
                            <SelectUI
                                name='globalCategory'
                                label='Global category'
                                darkLabel
                                placeholder='Select category'
                                options={GLOBAL_TICKET_STATUS_CATEGORY_OPTIONS}
                                value={findOption(
                                    GLOBAL_TICKET_STATUS_CATEGORY_OPTIONS,
                                    form.globalCategory
                                )}
                                onChange={({ value }) =>
                                    updateField("globalCategory", value)
                                }
                                isRequired
                            />
                        </div>
                        <Input
                            label='Status name'
                            labelClass='text-dark fw-medium'
                            inputClass='form-control'
                            name='name'
                            placeholder='e.g Awaiting Customer Response'
                            value={form.name}
                            maxLength={48}
                            onChange={({ target: { value } }) =>
                                updateField("name", value)
                            }
                        />
                        <Textarea
                            label='Description (Optional)'
                            labelClass='text-dark fw-medium'
                            inputClass='form-control'
                            name='description'
                            rows={4}
                            placeholder='Describe when agents should use this status.'
                            value={form.description}
                            onChange={({ target: { value } }) =>
                                updateField("description", value)
                            }
                        />
                        <div className='mb-4 d-flex align-items-center justify-content-between'>
                            <div>
                                <div className='fw-medium'>Active</div>
                                <small className='text-muted'>
                                    Inactive statuses can&apos;t be assigned to
                                    new tickets.
                                </small>
                            </div>
                            <Switch
                                id='create-cts-active'
                                checked={form.isActive}
                                onChange={(e) =>
                                    updateField("isActive", e.target.checked)
                                }
                            />
                        </div>
                        <Button
                            type='submit'
                            text='Create status'
                            classType='primary'
                            disabled={saving}
                            otherClass='w-100'
                        />
                    </form>
                </div>
            </div>
        </SideModal>
    );
};

export default CreateCustomTicketStatusModal;
